import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import type { User } from '@supabase/supabase-js';
import type { Database } from '../lib/supabase';
import {
  validateAndFormatUSPhone,
  generateOTP,
  validateOTP,
  createOTPExpiration,
  isOTPExpired,
} from '../lib/phoneValidation';
import { setUserContext, clearUserContext } from '../lib/sentry';
import { identifyUser, resetUser, trackSignup } from '../lib/analytics';

type Profile = Database['public']['Tables']['profiles']['Row'];

interface AuthState {
  user: User | null;
  profile: Profile | null;
  loading: boolean;
  initialized: boolean;
  isAdmin: () => boolean;
  signUp: (email: string, password: string, fullName: string, role: 'job_seeker' | 'employer') => Promise<{ id: string } | null>;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithPhone: (phoneNumber: string, password: string) => Promise<void>;
  sendOTPToPhone: (phoneNumber: string) => Promise<{ success: boolean; error?: string }>;
  verifyPhoneOTP: (phoneNumber: string, otp: string) => Promise<{ success: boolean; error?: string }>;
  signOut: () => Promise<void>;
  fetchProfile: () => Promise<void>;
  updateProfile: (updates: Partial<Profile>) => Promise<void>;
  initialize: () => Promise<void>;
  attemptProfileRepair: (userId: string, email: string) => Promise<void>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  profile: null,
  loading: true,
  initialized: false,

  isAdmin: () => {
    const { profile } = get();
    return profile?.role === 'admin';
  },

  initialize: async () => {
    try {
      console.log('[Auth] Starting initialization...');
      const { data: { session } } = await supabase.auth.getSession();

      if (session?.user) {
        console.log('[Auth] Session found, user ID:', session.user.id);
        set({ user: session.user, loading: true });

        try {
          await get().fetchProfile();

          const { profile } = get();
          if (profile) {
            console.log('[Auth] Profile loaded successfully:', profile.role);
          } else {
            console.warn('[Auth] Profile not found for user:', session.user.id);
            await get().attemptProfileRepair(session.user.id, session.user.email || '');

            // Check again after repair attempt
            const { profile: repairedProfile } = get();
            if (!repairedProfile) {
              console.error('[Auth] Profile repair failed - signing out user');
              await supabase.auth.signOut();
              set({ user: null, profile: null });
            }
          }
        } catch (profileError) {
          console.error('[Auth] Profile fetch failed during initialization:', profileError);
          // Sign out the user if profile can't be loaded
          await supabase.auth.signOut();
          set({ user: null, profile: null });
        }
      } else {
        console.log('[Auth] No active session');
      }

      supabase.auth.onAuthStateChange((_event, session) => {
        console.log('[Auth] State change event:', _event);

        (async () => {
          try {
            const prevUser = get().user;
            set({ user: session?.user ?? null });

            if (session?.user) {
              if (!prevUser || prevUser.id !== session.user.id) {
                await get().fetchProfile();

                // If profile still null after fetch, try repair
                const { profile } = get();
                if (!profile) {
                  await get().attemptProfileRepair(session.user.id, session.user.email || '');
                }
              }
            } else {
              set({ profile: null });
            }
          } catch (error) {
            console.error('[Auth] Error in auth state change handler:', error);
            set({ user: null, profile: null });
          }
        })();
      });

      set({ initialized: true, loading: false });
      console.log('[Auth] Initialization complete');
    } catch (error) {
      console.error('[Auth] Initialization error:', error);
      set({ initialized: true, loading: false });
    }
  },

  signUp: async (email, password, fullName, role) => {
    set({ loading: true });
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
      });

      if (error) throw error;

      if (data.user) {
        const { error: profileError } = await supabase
          .from('profiles')
          .insert({
            id: data.user.id,
            email,
            full_name: fullName,
            role,
          });

        if (profileError) throw profileError;

        const { error: creditsError } = await supabase
          .from('credits')
          .insert({
            user_id: data.user.id,
            balance: 100,
            total_earned: 100,
          });

        if (creditsError) throw creditsError;

        await supabase
          .from('credit_transactions')
          .insert({
            user_id: data.user.id,
            amount: 100,
            type: 'signup_bonus',
            description: 'Welcome bonus for signing up',
          });

        await get().fetchProfile();

        // Track signup in analytics
        trackSignup(role, {
          email,
          signup_method: 'email',
        });

        // Set user context for monitoring
        setUserContext({
          id: data.user.id,
          email,
          role,
        });

        identifyUser(data.user.id, {
          email,
          role,
          signupDate: new Date().toISOString(),
          creditsBalance: 100,
        });

        return { id: data.user.id };
      }

      return null;
    } finally {
      set({ loading: false });
    }
  },

  signIn: async (email, password) => {
    set({ loading: true });
    try {
      console.log('[Auth] Attempting sign in for:', email);
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        console.error('[Auth] Sign in failed:', error.message, error);
        throw error;
      }

      if (!data.user) {
        console.error('[Auth] Sign in succeeded but no user returned');
        throw new Error('Sign in failed: No user data returned');
      }

      console.log('[Auth] Sign in successful, user ID:', data.user.id);
      set({ user: data.user });

      try {
        await get().fetchProfile();
        const { profile } = get();

        if (!profile) {
          console.warn('[Auth] Profile not loaded after sign in, attempting repair');
          await get().attemptProfileRepair(data.user.id, data.user.email || '');

          const { profile: repairedProfile } = get();
          if (!repairedProfile) {
            console.error('[Auth] Profile repair failed after sign in');
            throw new Error('Failed to load or create user profile. Please contact support.');
          }
        }

        console.log('[Auth] Sign in complete with profile:', profile?.role || 'unknown');

        // Set user context for monitoring
        if (profile) {
          setUserContext({
            id: data.user.id,
            email: data.user.email || undefined,
            role: profile.role,
          });

          identifyUser(data.user.id, {
            email: data.user.email || undefined,
            role: profile.role,
          });
        }
      } catch (profileError: any) {
        console.error('[Auth] Profile fetch/repair error during sign in:', profileError);
        // Sign out the user if profile operations fail
        await supabase.auth.signOut();
        set({ user: null, profile: null });
        throw new Error(`Profile error: ${profileError.message || 'Failed to load profile'}`);
      }
    } catch (error: any) {
      console.error('[Auth] Sign in error:', error);
      throw error;
    } finally {
      set({ loading: false });
    }
  },

  signInWithPhone: async (phoneNumber, password) => {
    set({ loading: true });
    try {
      console.log('[Auth] Attempting phone sign in for:', phoneNumber);

      // Validate and format phone number
      const phoneValidation = validateAndFormatUSPhone(phoneNumber);
      if (!phoneValidation.valid) {
        throw new Error(phoneValidation.error || 'Invalid phone number');
      }

      const formattedPhone = phoneValidation.formatted!;

      // Find profile with this phone number
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('email, phone_verified')
        .eq('phone_number', formattedPhone)
        .maybeSingle();

      if (profileError || !profile) {
        console.error('[Auth] Phone number not found or error:', profileError);
        throw new Error('Phone number not registered or not verified');
      }

      if (!profile.phone_verified) {
        throw new Error('Phone number not verified. Please verify your phone number first.');
      }

      // Sign in using the email associated with the phone number
      const { data, error } = await supabase.auth.signInWithPassword({
        email: profile.email,
        password,
      });

      if (error) {
        console.error('[Auth] Phone sign in failed:', error.message);
        throw error;
      }

      if (!data.user) {
        throw new Error('Sign in failed: No user data returned');
      }

      console.log('[Auth] Phone sign in successful, user ID:', data.user.id);
      set({ user: data.user });

      await get().fetchProfile();
      console.log('[Auth] Phone sign in complete');
    } catch (error: any) {
      console.error('[Auth] Phone sign in error:', error);
      throw error;
    } finally {
      set({ loading: false });
    }
  },

  sendOTPToPhone: async (phoneNumber) => {
    try {
      console.log('[Auth] Sending OTP to:', phoneNumber);

      // Validate and format phone number
      const phoneValidation = validateAndFormatUSPhone(phoneNumber);
      if (!phoneValidation.valid) {
        return {
          success: false,
          error: phoneValidation.error || 'Invalid phone number',
        };
      }

      const formattedPhone = phoneValidation.formatted!;
      const { user } = get();

      if (!user) {
        return {
          success: false,
          error: 'User must be logged in to verify phone number',
        };
      }

      // Check rate limiting
      const { data: rateLimitCheck, error: rateLimitError } = await supabase
        .rpc('check_phone_rate_limit', {
          p_phone_number: formattedPhone,
          p_attempt_type: 'otp_send',
          p_max_attempts: 5,
          p_window_minutes: 60,
        });

      if (rateLimitError || !rateLimitCheck) {
        return {
          success: false,
          error: 'Too many OTP requests. Please try again later.',
        };
      }

      // Generate OTP code
      const otpCode = generateOTP();
      const expiresAt = createOTPExpiration(10); // 10 minutes

      // Store OTP in profile
      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          phone_verification_code: otpCode,
          phone_verification_expires_at: expiresAt,
        })
        .eq('id', user.id);

      if (updateError) {
        console.error('[Auth] Failed to store OTP:', updateError);
        return {
          success: false,
          error: 'Failed to send OTP. Please try again.',
        };
      }

      // Log the attempt
      await supabase.from('phone_auth_attempts').insert({
        phone_number: formattedPhone,
        attempt_type: 'otp_send',
        success: true,
      });

      // TODO: Integrate with SMS provider (Twilio, etc.)
      // For now, we'll just log the OTP (in production, this would send an SMS)
      console.log('[Auth] OTP Code (DEV MODE):', otpCode);
      console.log('[Auth] In production, this would be sent via SMS to:', formattedPhone);

      return {
        success: true,
      };
    } catch (error: any) {
      console.error('[Auth] Send OTP error:', error);
      return {
        success: false,
        error: error.message || 'Failed to send OTP',
      };
    }
  },

  verifyPhoneOTP: async (phoneNumber, otp) => {
    try {
      console.log('[Auth] Verifying OTP for:', phoneNumber);

      // Validate OTP format
      const otpValidation = validateOTP(otp);
      if (!otpValidation.valid) {
        return {
          success: false,
          error: otpValidation.error || 'Invalid OTP code',
        };
      }

      // Validate and format phone number
      const phoneValidation = validateAndFormatUSPhone(phoneNumber);
      if (!phoneValidation.valid) {
        return {
          success: false,
          error: phoneValidation.error || 'Invalid phone number',
        };
      }

      const formattedPhone = phoneValidation.formatted!;
      const { user } = get();

      if (!user) {
        return {
          success: false,
          error: 'User must be logged in to verify phone number',
        };
      }

      // Get current profile with OTP info
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('phone_verification_code, phone_verification_expires_at')
        .eq('id', user.id)
        .maybeSingle();

      if (profileError || !profile) {
        return {
          success: false,
          error: 'Failed to verify OTP. Please try again.',
        };
      }

      // Check if OTP exists
      if (!profile.phone_verification_code) {
        return {
          success: false,
          error: 'No OTP found. Please request a new code.',
        };
      }

      // Check if OTP is expired
      if (isOTPExpired(profile.phone_verification_expires_at)) {
        return {
          success: false,
          error: 'OTP code has expired. Please request a new code.',
        };
      }

      // Verify OTP code
      if (profile.phone_verification_code !== otp) {
        // Log failed attempt
        await supabase.from('phone_auth_attempts').insert({
          phone_number: formattedPhone,
          attempt_type: 'otp_verify',
          success: false,
        });

        return {
          success: false,
          error: 'Invalid OTP code. Please check and try again.',
        };
      }

      // OTP is valid - update profile
      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          phone_number: formattedPhone,
          phone_verified: true,
          phone_verification_code: null,
          phone_verification_expires_at: null,
        })
        .eq('id', user.id);

      if (updateError) {
        console.error('[Auth] Failed to update phone verification:', updateError);
        return {
          success: false,
          error: 'Failed to verify phone number. Please try again.',
        };
      }

      // Log successful verification
      await supabase.from('phone_auth_attempts').insert({
        phone_number: formattedPhone,
        attempt_type: 'otp_verify',
        success: true,
      });

      // Refresh profile
      await get().fetchProfile();

      console.log('[Auth] Phone verification successful');
      return {
        success: true,
      };
    } catch (error: any) {
      console.error('[Auth] Verify OTP error:', error);
      return {
        success: false,
        error: error.message || 'Failed to verify OTP',
      };
    }
  },

  signOut: async () => {
    set({ loading: true });
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;

      // Clear monitoring context
      clearUserContext();
      resetUser();

      set({ user: null, profile: null });
    } finally {
      set({ loading: false });
    }
  },

  fetchProfile: async () => {
    const { user } = get();
    if (!user) {
      console.log('[Auth] No user, skipping profile fetch');
      return;
    }

    console.log('[Auth] Fetching profile for user:', user.id);

    const maxRetries = 3;
    let retryCount = 0;

    while (retryCount < maxRetries) {
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .maybeSingle();

        if (error) {
          console.error('[Auth] Error fetching profile:', {
            message: error.message,
            details: error.details,
            hint: error.hint,
            code: error.code,
          });
          if (retryCount < maxRetries - 1) {
            retryCount++;
            const delay = Math.min(1000 * Math.pow(2, retryCount), 5000);
            console.log(`[Auth] Retrying profile fetch in ${delay}ms (attempt ${retryCount + 1}/${maxRetries})`);
            await new Promise(resolve => setTimeout(resolve, delay));
            continue;
          }
          throw error;
        }

        if (!data) {
          console.error('[Auth] Profile not found in database for user:', user.id);
          return;
        }

        console.log('[Auth] Profile fetched successfully:', data.role);
        set({ profile: data });
        return;
      } catch (error) {
        console.error('[Auth] Exception during profile fetch:', error);
        if (retryCount < maxRetries - 1) {
          retryCount++;
          const delay = Math.min(1000 * Math.pow(2, retryCount), 5000);
          console.log(`[Auth] Retrying after exception in ${delay}ms (attempt ${retryCount + 1}/${maxRetries})`);
          await new Promise(resolve => setTimeout(resolve, delay));
        } else {
          return;
        }
      }
    }
  },

  attemptProfileRepair: async (userId: string, email: string) => {
    console.log('[Auth] Attempting to repair profile for user:', userId);
    try {
      const { data: existingProfile } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (!existingProfile) {
        console.log('[Auth] No profile exists, creating one with default role');
        const { data: newProfile, error } = await supabase
          .from('profiles')
          .insert({
            id: userId,
            email: email,
            full_name: email.split('@')[0],
            role: 'job_seeker',
          })
          .select()
          .single();

        if (error) {
          console.error('[Auth] Failed to create profile:', error);
          return;
        }

        console.log('[Auth] Profile created successfully');
        set({ profile: newProfile });

        const { error: creditsError } = await supabase
          .from('credits')
          .insert({
            user_id: userId,
            balance: 100,
            total_earned: 100,
          });

        if (creditsError) {
          console.error('[Auth] Failed to create credits record:', creditsError);
        }
      }
    } catch (error) {
      console.error('[Auth] Profile repair failed:', error);
    }
  },

  updateProfile: async (updates) => {
    const { user } = get();
    if (!user) return;

    const { error } = await supabase
      .from('profiles')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', user.id);

    if (error) throw error;

    await get().fetchProfile();
  },
}));
